import React, {useEffect, useRef, useState} from 'react'
import {searchAddresses} from '../../application/api'
import {IAddress} from '../../application/models/address'

const MIN_ADDRESS_SEARCH_LENGTH = 3

interface AddressAutocompleteProps {
  label?: string
  placeholder?: string
  onSelect?: (id: string, address: string) => void
  onClear?: () => void
  onInputChange?: (value: string) => void
  inputClassName?: string
  initialValue?: string
  wrapperClassName?: string
  wizardSessionId?: string
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label = 'Please enter your address',
  placeholder = 'Start typing an address...',
  onSelect,
  onClear,
  onInputChange,
  inputClassName,
  initialValue,
  wrapperClassName = 'mb-10',
  wizardSessionId,
}) => {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<IAddress[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setQuery(initialValue ?? '')
  }, [initialValue])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    onInputChange?.(value)
    onClear?.()

    if (debounceRef.current) clearTimeout(debounceRef.current)

    const normalizedValue = value.trim()
    const normalizedWizardSessionId = wizardSessionId?.trim()
    if (
      !normalizedValue ||
      normalizedValue.length < MIN_ADDRESS_SEARCH_LENGTH ||
      !normalizedWizardSessionId
    ) {
      setSuggestions([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchAddresses(normalizedWizardSessionId, normalizedValue)
        setSuggestions(results)
      } catch {
        setSuggestions([])
      }
    }, 300)
  }

  const handleSelect = (item: IAddress) => {
    setQuery(item.address)
    setSuggestions([])
    onSelect?.(item.id, item.address)
  }

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setSuggestions([]), 150)
  }

  const handleMouseDown = (item: IAddress) => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    handleSelect(item)
  }

  return (
    <div className={wrapperClassName}>
      {label && <label className='form-label fw-bold fs-6 required'>{label}</label>}
      <div style={{position: 'relative'}}>
        <input
          type='text'
          className={`form-control form-control-lg form-control-solid${
            inputClassName ? ` ${inputClassName}` : ''
          }`}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onBlur={handleBlur}
          autoComplete='off'
        />
        {suggestions.length > 0 && (
          <ul
            className='list-unstyled mb-0 shadow'
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              background: '#fff',
              border: '1px solid #e4e6ef',
              borderTop: 'none',
              borderRadius: '0 0 0.475rem 0.475rem',
              maxHeight: '220px',
              overflowY: 'auto',
            }}
          >
            {suggestions.map((item) => (
              <li
                key={item.id}
                onMouseDown={() => handleMouseDown(item)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid #f1f3f8',
                }}
              >
                {item.address}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default AddressAutocomplete
